import React, { useContext, useEffect, useRef } from "react";
import { PropsWithChildren, createContext, useState } from "react";
import { TAnalyticsPayload, TAppParentMenuItem, TGraphData, TSelectedItem, TTimeZone, MetricsData, TAppSubMenuItem, TPaginationParamsWithLoading } from "../analytics";
import { adjustDateRangeWithCurrentTime, calculatePreviousPeriod, combineApiResponseToGraph, convertApiResponseToGraph, getFormattedDateTime, getDateWithMidnight, getStartAndEndOfPreviousYear } from "../AnalyticsHelper";
import { fetchAllGraphData, fetchLiveGuideModels } from "../Druid/DruidServices";
import { NONE, THIS_YEAR_1M, THIS_YEAR_1W, THIS_YEAR_2W, TODAY, LAST_30_DAYS, P1D } from "../AnalyticsConstants";
import { formatDate, formatDateRange, getDateRangeBasedTimeGrain, upperBoundDateformat } from "../MetricsHelper";
import { AppContext } from "Context/AppContext";
import dayjs from "utils/dayjs";

export const AnalyticsContext = createContext<any>(undefined);

export const AnalyticsContextProvider = ({ children }: PropsWithChildren) => {
    const menuItems = [
        { title: "Compatio", checked: true, isOpen: false, value: "Compatio" }, // Set Compatio as checked by default
        { title: "Bundler", checked: false, isOpen: false, value: "Bundler" },
        { title: "Commend (ARS)", checked: false, isOpen: false, value: "ARS" },
        { title: "Guide", checked: false, isOpen: false, value: "Guide" },
        { title: "Resolve", checked: false, isOpen: false, value: "Resolve" },
        { title: "SmartBuilder", checked: false, isOpen: false, value: "SmartBuilder" },
        { title: "XCS", checked: false, isOpen: false, value: "XCS" },
        { title: "XRS", checked: false, isOpen: false, value: "XRS" }
    ];

    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [showGraphLoader, setShowGraphLoader] = useState<boolean>(false);
    const [comingFromDetailPage, setComingFromDetailPage] = useState<boolean>(false);

    // No selectedMerchant state: it mirrored appContext.channelKey one-for-one. The channel is
    // read straight from AppContext below (see the channelKey const) so there is a single source.
    const [selectedRange, setSelectedRange] = useState<string>('');

    // Calculate the formatted date range for Last 30 days
    const getInitialDateRangeDisplay = () => {
        const today = dayjs().tz("UTC");
        const startDate = today.clone().subtract(30, 'days').toDate();
        const endDate = today.toDate();
        return formatDateRange(startDate, endDate, LAST_30_DAYS, "UTC");
    };

    // Initialize with the proper formatted date range as the value
    const [selectedItem, setSelectedItem] = useState<TSelectedItem>({
        label: LAST_30_DAYS,
        value: getInitialDateRangeDisplay()
    });

    const [selectedCompareMenuItem, setSelectedCompareMenuItem] = useState<string>("None");

    // Set Compatio as checked by default in menu data
    const [menuData, setMenuData] = useState<TAppParentMenuItem[]>(menuItems);
    const [selectedTimezone, setSelectedTimezone] = useState<TTimeZone>({ name: "UTC", abbreviation: "UTC" });
    // merchantData was removed with the channel dropdown: nothing ever read it, and the channel
    // now comes straight from appContext.channelKey.
    const [graphData, setGraphData] = useState<TGraphData[]>([]);
    const [applicationMetrics, setApplicationMetrics] = useState<MetricsData>({ data: [] });

    // Initialize payload with default values
    const [payload, setPayload] = useState<TAnalyticsPayload>({
        granularity: P1D,
        merchant_key: [],
        dateRange: [],
        compareTo: 'None',
        applications: ["Compatio"], // Set Compatio as the default application
        timezone: 'UTC',
        builder_ids: [],
        box_ids: []
    });

    const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
    const [guideModelsStatus, setGuideModelsStatus] = useState<'idle' | 'loading' | 'error' | 'loaded'>('idle');
    // Incremented on every fetch call and on clearSubmenuData so stale in-flight requests
    // (e.g. the previous call still waiting on its 10-second timeout) cannot overwrite
    // a newer successful result.
    const fetchSubMenuRequestId = useRef(0);
    const lastFetchedKey = useRef<string>('');
    // Survives no-models gap periods: true when the user's last Guide intent was "Select All".
    // Cleared on merchant change and when the user makes a partial/empty selection.
    const guideSelectAllActiveRef = useRef<boolean>(false);

    const [subMenuItems, setSubMenuItems] = useState<TAppSubMenuItem[]>([]);
    const [selectedSubMenuItems, setSelectedSubMenuItems] = useState<TAppSubMenuItem[]>([]);

    const [commendSubMenuItems, setCommendSubMenuItems] = useState<TAppSubMenuItem[]>([]);
    const [selectedCommendSubMenuItems, setSelectedCommendSubMenuItems] = useState<TAppSubMenuItem[]>([]);

    const ARSBoxData = {
        "1": "Box 1",
        "2": "Box 2",
        "3": "Box 3"
    };

    const [pagination, setPagination] = useState<TPaginationParamsWithLoading>({
        pageNumber: 1,
        pageSize: 10,
        hasMore: false,
        loading: false
    });
    const appContext = useContext(AppContext);
    const industryId = appContext.get_selected_industry() && appContext?.get_selected_industry()?.id;
    // The workspace Environment's channel key -- the only channel input this module has.
    const channelKey = appContext?.channelKey;

    const fetchCommendSubMenuItems = () => {
        const items = appContext.applications.get(appContext?.selectedParty?.name ?? "") || [];
        const isCommendARSSubscribed = items.includes("Commend (ARS)");

        if (!isCommendARSSubscribed) return [];

        const convertedItems: TAppSubMenuItem[] = Object.entries(ARSBoxData).map(([key, value]) => ({
            title: value,
            value: key,
            checked: false
        }));
        setCommendSubMenuItems(convertedItems);
        return convertedItems;
    }

    const fetchSubMenuItems = async () => {
        if (!industryId || !channelKey) {
            clearSubmenuData();
            return [];
        }
        // Fall back to last 30 days if the payload date range hasn't been initialised yet
        const dateRange = (payload.dateRange && payload.dateRange.length === 2)
            ? payload.dateRange
            : getDateRangeBasedTimeGrain(LAST_30_DAYS, selectedTimezone?.name || 'UTC');

        // Return cached result immediately when merchant, industry, and date range are
        // unchanged — avoids a redundant API call when the user re-opens the Guide submenu
        // without changing any filter conditions (Issue 4).
        const currentKey = `${industryId}|${channelKey}|${dateRange.join('|')}`;
        if (currentKey === lastFetchedKey.current && guideModelsStatus === 'loaded') {
            return subMenuItems;
        }

        fetchSubMenuRequestId.current += 1;
        const requestId = fetchSubMenuRequestId.current;

        setGuideModelsStatus('loading');
        try {
            const response = await fetchLiveGuideModels(
                industryId,
                channelKey,
                dateRange,
                selectedTimezone?.name || 'UTC'
            );
            // A newer call has superseded this one — discard its result.
            if (fetchSubMenuRequestId.current !== requestId) return [];
            if (!response?.errorfound && response?.data?.done) {
                const formattedItems = (response.data?.data ?? []).map((item: any) => ({
                    title: item.ISRName,
                    value: item.id,
                    checked: false
                }));
                // Capture BEFORE any state updates below (closure = old state values).
                // When subMenuItems is currently empty (no-models gap period), fall back to the
                // persisted flag so a prior "Select All" intent survives the gap.
                const wasAllSelected = subMenuItems.length > 0
                    ? selectedSubMenuItems.length > 0 && selectedSubMenuItems.length === subMenuItems.length
                    : guideSelectAllActiveRef.current;

                lastFetchedKey.current = currentKey;
                setSubMenuItems(formattedItems);
                setGuideModelsStatus('loaded');

                if (formattedItems.length > 0) {
                    if (wasAllSelected) {
                        // Select All was active — carry it forward to the new live-model list
                        // so models auto-select when returning from a no-models gap period.
                        setSelectedSubMenuItems(
                            formattedItems.map((item: any) => ({ ...item, checked: true }))
                        );
                        guideSelectAllActiveRef.current = true;
                    } else {
                        // Partial selection — keep only models still live in the new date range;
                        // stale entries (no longer live) are silently removed.
                        // Return the same reference when nothing changes (e.g. prev is already empty)
                        // to avoid triggering unnecessary payload cascades that abort in-flight API calls.
                        const liveIds = new Set(formattedItems.map((m: any) => m.value));
                        setSelectedSubMenuItems((prev: TAppSubMenuItem[]) => {
                            const filtered = prev.filter(item => liveIds.has(item.value));
                            return filtered.length === prev.length ? prev : filtered;
                        });
                        guideSelectAllActiveRef.current = false;
                    }
                } else {
                    // No live models in this period — clear stale chips but preserve the
                    // guideSelectAllActiveRef so intent survives the no-models gap.
                    // Use functional form to avoid a new [] reference when already empty.
                    setSelectedSubMenuItems((prev: TAppSubMenuItem[]) => prev.length === 0 ? prev : []);
                }
                return formattedItems;
            } else {
                setSubMenuItems([]);
                setGuideModelsStatus('error');
                return [];
            }
        } catch (error) {
            // Same guard for the error path (e.g. the 10-second timeout finally fires).
            if (fetchSubMenuRequestId.current !== requestId) return [];
            setSubMenuItems([]);
            setGuideModelsStatus('error');
            console.error('Error fetching guide live models:', error);
            return [];
        }
    };

    const retryFetchGuideModels = () => {
        setGuideModelsStatus('idle');
        fetchSubMenuItems();
    };

    // Set initial date range based on default selection
    useEffect(() => {
        const dateRange = getDateRangeBasedTimeGrain(LAST_30_DAYS, "UTC");
        setPayload(prev => ({ ...prev, dateRange }));

        // Also set the formatted display value
        const today = dayjs().tz("UTC");
        const startDate = today.clone().subtract(30, 'days').toDate();
        const endDate = today.toDate();
        const formattedRange = formatDateRange(startDate, endDate, LAST_30_DAYS, "UTC");

        setSelectedItem(prev => ({ ...prev, value: formattedRange }));
    }, []);

    // Used to auto-fire from a useEffect([channelKey]) here -- since this provider is mounted
    // globally (App.tsx wraps <RouterProvider> with it), that fired fetchLiveGuideModels on every
    // route and every channel switch app-wide, even though the real consumers are the Performance
    // pages. Moved into this one function; the actual trigger now lives in AnalyticsPage.tsx and
    // AnalyticsDetailPage.tsx, each in their own useEffect([channelKey]).
    const refreshGuideSubMenu = () => {
        setMenuData(menuItems)
        clearSubmenuData();
        fetchCommendSubMenuItems();
        if (channelKey) {
            const subscribedApps = appContext.applications.get(appContext?.selectedParty?.name ?? "") || [];
            if (subscribedApps.includes("Guide")) {
                fetchSubMenuItems();
            }
        }
    };

    useEffect(() => {
        ensureDefaultApplication();
    }, [payload.applications]);

    const ensureDefaultApplication = (): boolean => {
        if (!payload.applications?.length) {
            setPayload((prev: TAnalyticsPayload) => ({
                ...prev,
                applications: ["Compatio"]
            }));
            return true;
        }
        return false;
    };

    const clearAllTheFields = () => {
        setSelectedCompareMenuItem("None");
        setMenuData(menuItems);
        setComingFromDetailPage(false);

        // Reset to default values instead of empty values
        setPayload({
            granularity: P1D,
            merchant_key: [],
            dateRange: getDateRangeBasedTimeGrain(LAST_30_DAYS, "UTC"),
            compareTo: 'None',
            applications: ["Compatio"],
            timezone: 'UTC',
            builder_ids: [],
            box_ids: []
        });

        clearGraphAndMetricsData();
        setSelectedTimezone({ name: "UTC", abbreviation: "UTC" });

        // Reset to default selected item
        setSelectedItem({ label: LAST_30_DAYS, value: getInitialDateRangeDisplay() });

        clearSubmenuData();
    }

    const clearSubmenuData = () => {
        fetchSubMenuRequestId.current += 1;
        lastFetchedKey.current = '';
        guideSelectAllActiveRef.current = false;
        setSubMenuItems([]);
        setSelectedSubMenuItems([]);
        setCommendSubMenuItems([]);
        setSelectedCommendSubMenuItems([]);
        setGuideModelsStatus('idle');
        setPagination({
            pageNumber: 1,
            pageSize: 10,
            hasMore: false,
            loading: false
        });
    }

    const clearGraphAndMetricsData = () => {
        setApplicationMetrics({ data: [] });
        setGraphData([]);
    }

    const setDateRange = (timeZoneName: string) => {
        let dateRange = getDateRangeBasedTimeGrain(selectedItem?.label, timeZoneName);
        setPayload({ ...payload, dateRange: dateRange })
    }

    const fetchGraphData = async () => {

        try {
            setShowGraphLoader(true);
            ensureDefaultApplication();
            let dateRange = getDateRangeBasedTimeGrain(selectedItem?.label, selectedTimezone?.name);
            if (selectedCompareMenuItem !== NONE) {
                let preDateRange = [];
                if (selectedItem.label === THIS_YEAR_1M || selectedItem.label === THIS_YEAR_1W) {
                    const { startOfYear, endOfYear } = getStartAndEndOfPreviousYear(selectedTimezone?.name);
                    preDateRange = [formatDate(startOfYear, selectedTimezone?.name), upperBoundDateformat(endOfYear, selectedTimezone?.name)]
                } else if (selectedItem.label === TODAY) {
                    preDateRange = calculatePreviousPeriod(dateRange, payload.granularity, selectedItem.label, selectedTimezone?.name);
                    dateRange = [getDateWithMidnight(selectedTimezone?.name), getFormattedDateTime(selectedTimezone?.name)];
                } else {
                    preDateRange = calculatePreviousPeriod(dateRange, payload.granularity, selectedItem.label, selectedTimezone?.name);
                }
                const updateState = { ...payload, dateRange: preDateRange };
                const updateStateCP = { ...payload, dateRange: dateRange };
                const currentPeriodData = await fetchAllGraphData(updateStateCP);
                if (currentPeriodData === null) {
                    return;
                }
                const previousPeriodData = await fetchAllGraphData(updateState)
                if (previousPeriodData === null) {
                    return;
                }
                const convertedResponse = combineApiResponseToGraph(currentPeriodData, previousPeriodData, payload.granularity);
                setGraphData(convertedResponse);
            } else {
                let updatedDateRange = dateRange;
                if (selectedItem.label === TODAY) {
                    updatedDateRange = [getDateWithMidnight(selectedTimezone?.name), getFormattedDateTime(selectedTimezone?.name)];
                } else if ([THIS_YEAR_1M, THIS_YEAR_1W, THIS_YEAR_2W].includes(selectedItem.label)) {
                    updatedDateRange = adjustDateRangeWithCurrentTime(payload.dateRange, selectedTimezone?.name);
                }
                const updateState = { ...payload, dateRange: updatedDateRange };
                const response = await fetchAllGraphData(updateState);
                if (response === null) {
                    setGraphData([]);
                    return;
                }
                const result = convertApiResponseToGraph(response, payload.granularity);
                setGraphData(result);
            }
        } catch (error) {
            setShowGraphLoader(false);
            console.error("fetchGraphData ", error);
        } finally {
            setShowGraphLoader(false);
        }
    }

    return (
        <AnalyticsContext.Provider value=
            {{
                payload,
                setPayload,
                selectedItem,
                setSelectedItem,
                selectedCompareMenuItem,
                setSelectedCompareMenuItem,
                menuData,
                setMenuData,
                clearAllTheFields,
                applicationMetrics,
                setApplicationMetrics,
                showLoader,
                setShowLoader,
                showGraphLoader,
                setShowGraphLoader,
                graphData,
                setGraphData,
                comingFromDetailPage,
                setComingFromDetailPage,
                fetchGraphData,
                setSelectedTimezone,
                selectedTimezone,
                menuItems,
                clearGraphAndMetricsData,
                selectedRange,
                setSelectedRange,
                setDateRange,
                subMenuItems,
                setSubMenuItems,
                pagination,
                setPagination,
                fetchSubMenuItems,
                openSubmenu,
                setOpenSubmenu,
                selectedSubMenuItems,
                setSelectedSubMenuItems,
                commendSubMenuItems,
                setCommendSubMenuItems,
                selectedCommendSubMenuItems,
                setSelectedCommendSubMenuItems,
                fetchCommendSubMenuItems,
                ensureDefaultApplication,
                guideModelsStatus,
                retryFetchGuideModels,
                refreshGuideSubMenu
            }}>
            {children}
        </AnalyticsContext.Provider>
    );
}
